/**
 * Real API performance metrics sourced from TanStack Query's own QueryCache
 * event stream (a public, documented API), replacing the metrics the old
 * enhanced API service used to track in its own axios interceptors (task
 * 4.4b deleted that service; PerformanceMonitor/ApiTestPanel needed a real
 * replacement source for the three metrics the owner decided to keep --
 * averageResponseTime, cacheHitRate, cacheSize -- rather than an invented
 * number).
 *
 * This does not wrap queryFns or duplicate a transport layer: it only
 * listens to events TanStack already emits for every query's lifecycle.
 */
import type { QueryClient } from '@tanstack/react-query';

export interface QueryMetricsSnapshot {
  averageResponseTime: number;
  cacheHitRate: number;
  cacheSize: number;
}

interface MetricsState {
  fetchStartTimes: Map<string, number>;
  totalResponseTime: number;
  completedFetchCount: number;
  hitCount: number;
  missCount: number;
}

// One MetricsState per QueryClient instance (a WeakMap, not a module-level
// singleton) so tests that create a fresh QueryClient per test -- the
// pattern this task's other suites follow -- don't leak counters into each
// other or into the app's real client.
const stateByClient = new WeakMap<QueryClient, MetricsState>();

function attach(queryClient: QueryClient): MetricsState {
  const existing = stateByClient.get(queryClient);
  if (existing) {
    return existing;
  }

  const state: MetricsState = {
    fetchStartTimes: new Map(),
    totalResponseTime: 0,
    completedFetchCount: 0,
    hitCount: 0,
    missCount: 0,
  };

  queryClient.getQueryCache().subscribe((event) => {
    // A component started observing this query. If it already has fresh
    // data and nothing is fetching, that's a real cache hit -- the
    // observer was satisfied without a network request.
    if (event.type === 'observerAdded') {
      const { status, fetchStatus } = event.query.state;
      if (status === 'success' && fetchStatus === 'idle') {
        state.hitCount++;
      }
      return;
    }

    if (event.type !== 'updated') {
      return;
    }

    const hash = event.query.queryHash;
    if (event.action.type === 'fetch') {
      state.fetchStartTimes.set(hash, performance.now());
      state.missCount++;
    } else if (event.action.type === 'success' || event.action.type === 'error') {
      const start = state.fetchStartTimes.get(hash);
      if (start !== undefined) {
        state.totalResponseTime += performance.now() - start;
        state.completedFetchCount++;
        state.fetchStartTimes.delete(hash);
      }
    }
  });

  stateByClient.set(queryClient, state);
  return state;
}

/**
 * A point-in-time read of the three metrics. Safe to call repeatedly (e.g.
 * from a poll) -- `attach` is idempotent per QueryClient instance.
 *
 * Known approximation: `cacheHitRate` counts `useQuery`/observer-driven
 * reads (the courses list). Imperative `queryClient.fetchQuery` reads
 * (course details/history, called from callbacks rather than mounted as a
 * hook) never emit `observerAdded`, so a cache hit taken through that path
 * contributes to neither hitCount nor missCount -- only an actual network
 * fetch there registers, as a miss. This biases the reported hit rate
 * low rather than inventing an inflated one; documented here rather than
 * built further out given this task's scope.
 */
export function getQueryMetricsSnapshot(queryClient: QueryClient): QueryMetricsSnapshot {
  const state = attach(queryClient);
  const totalObservations = state.hitCount + state.missCount;

  return {
    averageResponseTime: state.completedFetchCount > 0
      ? state.totalResponseTime / state.completedFetchCount
      : 0,
    cacheHitRate: totalObservations > 0 ? state.hitCount / totalObservations : 0,
    cacheSize: queryClient.getQueryCache().getAll().length,
  };
}
